import { useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { Item } from "../../util/Item";
import { catalogMinSellPrice } from "../../util/salePrice";
import { updateActiveTask, Util } from "../../util/Util";
import { MONEY_LIMITS, QTY_LIMITS, roundQty } from "../../util/quantity";
import { useSale } from "./SaleContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useUI } from "../../providers/MoleUIContext";
import {
	isTillStockChecked,
	isNegativeStockAllowed,
	shouldEnforceStockQuantity,
} from "../../components/quantityStepperStock";

type CatalogItem = {
	id: number;
	ItemCode?: string;
	ItemName?: string;
	SellingPrice?: number;
	Stock?: number;
	Category?: string;
	IsMetered?: unknown;
	IsFractional?: unknown;
	MinSellPrice?: number;
};

/**
 * Quick-add a catalog item to the active POS cart (qty 1, merge lines).
 * Mirrors SaleItemDetail stock / auto-add rules for visual & split layouts.
 */
export function usePosQuickAdd(bundle?: any) {
	const { setPendingTasks, activeTask, items, apiRef } = useSale();
	const { appSettings } = useAppSocket();
	const { t } = useUI();
	const toastIdRef = useRef<string | number>(-1);
	const cartItemsRef = useRef<Item[]>([]);

	cartItemsRef.current = [...(activeTask?.items ?? [])];

	const allowNegativeStock = isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value);

	const getActiveStock = useCallback((itemId: number) => {
		const catalogItem = items.find((entry: any) => entry.id === itemId);
		const catalogStock = catalogItem?.Stock ?? 0;
		const bundleStock = bundle
			? bundle.reduce((sum: number, row: any) => (
				parseInt(row.item_id, 10) === itemId ? sum + row.quantity : sum
			), 0)
			: 0;
		const taskQty = cartItemsRef.current.reduce((sum, row) => (
			row.id === itemId ? sum + row.quantity : sum
		), 0);
		return roundQty(bundleStock + catalogStock - taskQty);
	}, [bundle, items]);

	const addCatalogItem = useCallback((catalog: CatalogItem) => {
		if (!activeTask || !catalog?.id) return false;

		const price = Number(catalog.SellingPrice ?? 0);
		const item = new Item({
			id: catalog.id,
			item_code: catalog.ItemCode,
			item: catalog.ItemName,
			price,
			stock: catalog.Stock,
			quantity: 1,
			category: catalog.Category,
			isFractional: catalog.IsFractional === true,
			minSellPrice: catalogMinSellPrice(catalog),
		});

		const catalogEntry = items.find((entry: any) => entry.id === catalog.id) ?? catalog;
		const metered = isTillStockChecked(catalogEntry);
		const stock = getActiveStock(catalog.id);

		if (price < 0 || price > MONEY_LIMITS.max) {
			toast.error(t("common:validation.amountRange", {
				max: Util.formatNumber(MONEY_LIMITS.max),
			}));
			return false;
		}

		if (
			shouldEnforceStockQuantity({ isMetered: metered, allowNegativeStock })
			&& 1 > stock
		) {
			toast.error(t("common:validation.insufficientStock"));
			return false;
		}

		let added = false;

		updateActiveTask(setPendingTasks, (prev) => {
			const cartItems = prev.items ?? [];
			const existingIndex = cartItems.findIndex(
				(row) => Util.compareStrings(row.uniqueId, item.uniqueId),
			);

			const mergedRow = new Item({ ...item });

			if (existingIndex > -1) {
				mergedRow.quantity = roundQty(mergedRow.quantity + cartItems[existingIndex].quantity);

				if (mergedRow.quantity > QTY_LIMITS.max) {
					const rowIndex = apiRef.current
						? apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId) + 1
						: existingIndex + 1;
					toast.error(t("common:validation.mergeExceedsLimit", {
						row: rowIndex,
						max: Util.formatNumber(QTY_LIMITS.max),
					}));
					return {};
				}

				if (
					shouldEnforceStockQuantity({ isMetered: metered, allowNegativeStock })
				) {
					const othersQty = cartItems.reduce((sum, row, idx) => (
						idx !== existingIndex && row.id === item.id ? sum + row.quantity : sum
					), 0);
					const catalogStock = Number(catalogEntry?.Stock ?? 0);
					const bundleStock = bundle
						? bundle.reduce((sum: number, row: any) => (
							parseInt(row.item_id, 10) === item.id ? sum + row.quantity : sum
						), 0)
						: 0;
					const available = roundQty(catalogStock + bundleStock - othersQty);
					if (mergedRow.quantity > available) {
						toast.error(t("common:validation.insufficientStock"));
						return {};
					}
				}

				if (apiRef.current) {
					const oldIndex = apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId);
					const rowIndex = oldIndex + 1;
					const msg = t("common:info.mergedWithRow", {
						row: rowIndex,
						quantity: mergedRow.quantity,
					});
					if (!toast.isActive(toastIdRef.current)) {
						toastIdRef.current = toast.info(msg, { autoClose: 5000 });
					} else {
						toast.update(toastIdRef.current, { render: msg, autoClose: 5000 });
					}
				}
			} else if (
				shouldEnforceStockQuantity({ isMetered: metered, allowNegativeStock })
				&& item.quantity > stock
			) {
				toast.error(t("common:validation.insufficientStock"));
				return {};
			}

			const updatedItems = existingIndex > -1
				? cartItems.map((cartItem, idx) => (idx === existingIndex ? mergedRow : cartItem))
				: [...cartItems, mergedRow];

			cartItemsRef.current = updatedItems;
			added = true;
			return { items: updatedItems };
		});

		return added;
	}, [
		activeTask,
		allowNegativeStock,
		apiRef,
		bundle,
		getActiveStock,
		items,
		setPendingTasks,
		t,
	]);

	return { addCatalogItem };
}
